import { inject, Injectable } from '@angular/core';
import { Mail } from '@common/models';
import { MailOdataService } from '@common/services';
import { getCustomSizeItems, getUniqueListByProp, MailTag, MailTagsMap } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { I18NextPipe } from 'angular-i18next';
import { ModelTemplate, naturalSort, SmartSearchState } from 'mbs-ui-kit';
import { asapScheduler, Observable, of, scheduled } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateMailService extends SmartSearchTemplatesBase {
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly mailService = inject(MailOdataService);

  readonly #backupArchive = MailTagsMap.get(MailTag.BackupArchive);
  readonly #dateFrom = MailTagsMap.get(MailTag.DateFrom);
  readonly #dateTo = MailTagsMap.get(MailTag.DateTo);
  readonly #from = MailTagsMap.get(MailTag.From);
  readonly #sizeLess = MailTagsMap.get(MailTag.SizeLess);
  readonly #sizeMore = MailTagsMap.get(MailTag.SizeMore);
  readonly #subject = MailTagsMap.get(MailTag.Subject);
  readonly #to = MailTagsMap.get(MailTag.To);
  readonly #withAttachment = MailTagsMap.get(MailTag.WithAttachment);

  readonly #optionYes = this.i18nPipe.transform('common.yes', { format: 'capitalize' });
  readonly #optionNo = this.i18nPipe.transform('common.no', { format: 'capitalize' });

  private keyCache = 'mail-name_';

  public readonly FromTag: ModelTemplate<Mail> = {
    tag: this.#from.tag,
    items: (state): Observable<Mail[]> => this.getSearchMails(state, this.#from.prop),
    itemFormatter: ({ From }): string => From,
    addGroupBrackets: true
  };

  public readonly ToTag: ModelTemplate<Mail> = {
    tag: this.#to.tag,
    items: (state): Observable<Mail[]> => this.getSearchMails(state, this.#to.prop),
    itemFormatter: ({ To }): string => To,
    addGroupBrackets: true
  };

  public readonly SubjectTag: ModelTemplate<Mail> = {
    tag: this.#subject.tag,
    items: (state): Observable<Mail[]> => this.getSearchMails(state, this.#subject.prop),
    itemFormatter: ({ Subject }): string => Subject,
    addGroupBrackets: true
  };

  public readonly SizeMoreThanTag: ModelTemplate<string> = {
    tag: this.#sizeMore.tag,
    items: (state): Observable<string[]> => getCustomSizeItems(state, this.updateMailFilter.bind(this)),
    itemFormatter: (value: string): string => value,
    addGroupBrackets: true
  };

  public readonly SizeMoreLessTag: ModelTemplate<string> = {
    tag: this.#sizeLess.tag,
    items: (state): Observable<string[]> => getCustomSizeItems(state, this.updateMailFilter.bind(this)),
    itemFormatter: (value: string): string => value,
    addGroupBrackets: true
  };

  public readonly WithAttachmentTag: ModelTemplate<string> = {
    tag: this.#withAttachment.tag,
    items: (): Observable<string[]> => of<string[]>([this.#optionYes, this.#optionNo]),
    itemFormatter: (model): string => model
  };

  public readonly BackupArchiveTag: ModelTemplate<string> = {
    tag: this.#backupArchive.tag,
    items: (): Observable<string[]> => of<string[]>([this.#optionYes, this.#optionNo]),
    itemFormatter: (model): string => model
  };

  public readonly DateToTag: ModelTemplate<Mail> = {
    tag: this.#dateTo.tag,
    items: (): Observable<Mail[]> => of<Mail[]>([]),
    addGroupBrackets: true
  };

  public readonly DateFromTag: ModelTemplate<Mail> = {
    tag: this.#dateFrom.tag,
    items: (): Observable<Mail[]> => of<Mail[]>([]),
    addGroupBrackets: true
  };

  private getSearchMails(state: SmartSearchState, prop: string): Observable<Mail[]> {
    const term = state.leftCaretValue;
    const request = this.getSearchRequest(term, prop);

    return scheduled(
      request.pipe(
        tap(() => this.writeToCache(this.keyCache + term)),
        map((mails) => {
          const filtered = mails.filter((stat) => stat[prop].toLowerCase().includes(term));
          return getUniqueListByProp(filtered, prop)
            .toSorted((a, b) => naturalSort(a[prop].toLowerCase(), b[prop].toLowerCase()))
            .slice(0, 10);
        })
      ),
      asapScheduler
    );
  }

  private getSearchRequest(term: string, prop: string): Observable<Mail[]> {
    switch (prop) {
      case MailTag.From:
        return this.mailService.getEmailsInFromSection(term);
      case MailTag.To:
        return this.mailService.getEmailsInToSection(term);
      case MailTag.Subject:
        return this.mailService.getListOfSubjects(term);
      default:
        return of([]);
    }
  }

  private updateMailFilter(term: string): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.mailService.filter = term || '';
  }
}
